import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Typography, message, Alert, Row, Col, Table, Tooltip, Space } from 'antd';
import { EditOutlined, SaveOutlined, UndoOutlined, FileTextOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const PassthroughConfig = ({ 
    fileColumns, 
    originalFilename,
    onConfigComplete 
}) => {
    const [customFilename, setCustomFilename] = useState('');
    const [columnRenames, setColumnRenames] = useState({});
    const [editingColumn, setEditingColumn] = useState(null);
    const [tempColumnName, setTempColumnName] = useState('');

    useEffect(() => {
        // Initialize with original filename (without extension)
        if (originalFilename) {
            const nameWithoutExt = originalFilename.replace(/\.[^/.]+$/, '');
            setCustomFilename(nameWithoutExt);
        }
        
        // Initialize column renames with original names
        const initialRenames = {};
        fileColumns.forEach(col => {
            initialRenames[col] = col;
        });
        setColumnRenames(initialRenames);
    }, [originalFilename, fileColumns]);

    const handleColumnRename = (originalName, newName) => {
        setColumnRenames(prev => ({
            ...prev,
            [originalName]: newName || originalName
        }));
    };

    const startEditing = (columnName) => {
        setEditingColumn(columnName);
        setTempColumnName(columnRenames[columnName] || columnName);
    };

    const saveColumnEdit = (originalName) => {
        handleColumnRename(originalName, tempColumnName);
        setEditingColumn(null);
        setTempColumnName('');
    };

    const cancelColumnEdit = () => {
        setEditingColumn(null);
        setTempColumnName('');
    };

    const resetColumnName = (originalName) => {
        handleColumnRename(originalName, originalName);
    };

    const resetAllColumns = () => {
        const resetRenames = {};
        fileColumns.forEach(col => {
            resetRenames[col] = col;
        });
        setColumnRenames(resetRenames);
        message.info('All column names reset to original');
    };

    const handleComplete = () => {
        if (!customFilename.trim()) {
            message.error('Please enter a filename');
            return;
        }

        // Filter out unchanged column names and create rename mapping
        const changedRenames = {};
        Object.entries(columnRenames).forEach(([original, renamed]) => {
            if (original !== renamed && renamed.trim()) {
                changedRenames[original] = renamed.trim();
            }
        });

        onConfigComplete({
            customFilename: customFilename.trim(),
            columnRenames: changedRenames
        });
        
        message.success('Configuration saved! Ready to process file.');
    };

    const columns = [
        {
            title: 'Original Column',
            dataIndex: 'original',
            key: 'original',
            width: '40%',
            render: (text) => (
                <Text code style={{ fontSize: 13 }}>{text}</Text>
            )
        },
        {
            title: 'New Column Name',
            dataIndex: 'renamed',
            key: 'renamed',
            width: '40%',
            render: (text, record) => {
                if (editingColumn === record.original) {
                    return (
                        <Input
                            value={tempColumnName}
                            onChange={(e) => setTempColumnName(e.target.value)}
                            onPressEnter={() => saveColumnEdit(record.original)}
                            onBlur={() => saveColumnEdit(record.original)}
                            autoFocus
                            size="small"
                            style={{ width: '100%' }}
                        />
                    );
                }
                return (
                    <Text 
                        style={{ 
                            color: record.original !== text ? '#1890ff' : 'inherit',
                            fontWeight: record.original !== text ? 500 : 'normal'
                        }}
                    >
                        {text}
                    </Text>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '20%',
            render: (_, record) => (
                <Space>
                    {editingColumn === record.original ? (
                        <>
                            <Button 
                                type="primary" 
                                size="small" 
                                icon={<SaveOutlined />}
                                onClick={() => saveColumnEdit(record.original)}
                            />
                            <Button 
                                size="small"
                                onClick={cancelColumnEdit}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <>
                            <Tooltip title="Edit column name">
                                <Button 
                                    type="text" 
                                    size="small" 
                                    icon={<EditOutlined />}
                                    onClick={() => startEditing(record.original)}
                                />
                            </Tooltip>
                            {record.original !== columnRenames[record.original] && (
                                <Tooltip title="Reset to original">
                                    <Button 
                                        type="text" 
                                        size="small" 
                                        icon={<UndoOutlined />}
                                        onClick={() => resetColumnName(record.original)}
                                    />
                                </Tooltip>
                            )}
                        </>
                    )}
                </Space>
            )
        }
    ];

    const tableData = fileColumns.map((col, index) => ({
        key: index,
        original: col,
        renamed: columnRenames[col] || col
    }));

    const changedColumnsCount = Object.entries(columnRenames).filter(
        ([orig, renamed]) => orig !== renamed
    ).length;

    return (
        <div>
            <Title level={4}>
                <FileTextOutlined style={{ marginRight: 8 }} />
                Configure Passthrough Upload
            </Title>
            <Text type="secondary">
                Customize the output filename and rename columns as needed. 
                The file will be uploaded as-is without template validation.
            </Text>

            <Alert
                message="Passthrough Mode"
                description="This mode allows you to upload your file directly without mapping to a predefined template. You can customize the filename and rename columns before sending to Invent."
                type="info"
                showIcon
                style={{ marginTop: 16, marginBottom: 24 }}
            />

            <Card title="Output Filename" style={{ marginBottom: 24 }}>
                <Row gutter={16} align="middle">
                    <Col flex="auto">
                        <Input
                            placeholder="Enter custom filename"
                            value={customFilename}
                            onChange={(e) => setCustomFilename(e.target.value)}
                            addonAfter=".csv"
                            size="large"
                            style={{ maxWidth: 500 }}
                        />
                    </Col>
                    <Col>
                        <Text type="secondary">
                            Final: <Text code>{customFilename || 'filename'}_[timestamp].csv</Text>
                        </Text>
                    </Col>
                </Row>
            </Card>

            <Card 
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Column Names ({fileColumns.length} columns)</span>
                        {changedColumnsCount > 0 && (
                            <Button 
                                size="small" 
                                icon={<UndoOutlined />}
                                onClick={resetAllColumns}
                            >
                                Reset All
                            </Button>
                        )}
                    </div>
                }
                style={{ marginBottom: 24 }}
            >
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Click the edit icon to rename columns. Changes are optional.
                    {changedColumnsCount > 0 && (
                        <Text strong style={{ marginLeft: 8, color: '#1890ff' }}>
                            ({changedColumnsCount} column{changedColumnsCount > 1 ? 's' : ''} renamed)
                        </Text>
                    )}
                </Text>

                <Table
                    dataSource={tableData}
                    columns={columns}
                    pagination={fileColumns.length > 10 ? { pageSize: 10 } : false}
                    size="small"
                    scroll={{ y: 400 }}
                />
            </Card>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Button
                    type="primary"
                    size="large"
                    icon={<SaveOutlined />}
                    onClick={handleComplete}
                    disabled={!customFilename.trim()}
                >
                    Save Configuration & Continue
                </Button>
            </div>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Text type="secondary">
                    {changedColumnsCount > 0 
                        ? `${changedColumnsCount} column name${changedColumnsCount > 1 ? 's' : ''} will be changed`
                        : 'No column names changed - file will keep original column names'
                    }
                </Text>
            </div>
        </div>
    );
};

export default PassthroughConfig;


